import React, { useEffect, useRef, useState } from 'react';
import config from '../config/config';

import { useOnClickOutside } from '../hooks/useOnClickOutside';
import { fetchAddress } from '../services/locationInfoService';
import { fetchNearestStations } from '../services/locationInfoService';
import { formatDistance } from '../services/locationInfoService';
import { fetchElevation } from '../services/elevationService';
import { fetchWeather } from '../services/weatherInfoService';
import { fetchMapCode } from '../services/mapCodeService';
import { copyToClipboard } from '../services/clipboardService';

const ICON_URLS = {
  close: `${config.icons.baseUrl}${config.icons.close}`,
  directionsTrain: `${config.icons.baseUrl}${config.icons.directionsTrain}`,
  mountain: `${config.icons.baseUrl}${config.icons.mountain}`,
  cloud: `${config.icons.baseUrl}${config.icons.cloud}`,
  nearMe: `${config.icons.baseUrl}${config.icons.nearMe}`,
  location: `${config.icons.baseUrl}${config.icons.location}`,
  map: `${config.icons.baseUrl}${config.icons.map}`,

  star: `${config.icons.baseUrl}${config.icons.star}`,
  share: `${config.icons.baseUrl}${config.icons.share}`,
  arrowTopRight: `${config.icons.baseUrl}${config.icons.arrowTopRight}`,
};

// TODO:後で整理
const DEFAULT_COORD_PRECISION = config.map.defaultCoordPrecision;

const JUMPTO_DURATION_MS = config.map.animationDurationMs;
const ADDRESS_GETTING_LABEL = "住所を取得中...";
const STATION_GETTING_LABEL = "最寄駅を取得中...";
const STATION_UNKNOWN_LABEL = "--";

const ELEVATION_GETTING_LABEL = "標高取得中...";
const ELEVATION_UNKNOWN_LABEL = config.map.elev.unknownLabel;

const WEATHER_ICON_BASE_URL = config.weather.iconBaseURL;
const WEATHER_ICON_EXT = config.weather.iconExt;

const TEMPERATURE_UNKNOWN_LABEL = config.weather.temperatureUnknownLabel;
const DEGREE_SYMBOL = config.weather.degreeSymbol;

const DISTANCE_GETTING_LABEL = "距離取得中...";
const DISTANCE_FROM_CURRENT_LOCATION_LABEL = "現在地から";

const MAPCODE_LABEL = "マップコード: ";
const MAPCODE_GETTING_LABEL = "検索中...";

const BUTTON_LABEL_SAVE = "保存";
const BUTTON_LABEL_SHARE = "共有";
const BUTTON_LABEL_ROUTE = "ルート";

// 特にここから整理
const LONG_PRESS_MARKER_COLOR = '#FF0000';
const EARTH_RADIUS_KM = 6371;
const DEG_TO_RAD = Math.PI / 180;
const DISTANCE_KM_DECIMALS = 1;

// 文字列仕様
const MAPCODE_PREFIX = 'mapcode: ';
const STATION_LIST_SEPARATOR = ' / ';
const STATION_DISTANCE_PREFIX = '約';
// 単位
const UNIT_METER = 'm';
const UNIT_KILOMETER = 'km';

// clipboardService に渡す “種別” (他ファイルでも使うなら特に定数化向き)
const CLIPBOARD_KIND_COORD = 'coord';
const CLIPBOARD_KIND_MAPCODE = 'mapcode';
const CLIPBOARD_KIND_URL = 'url';
const WEATHER_ICON_ALT = 'Weather Icon';

/**
 * 長押しポップアップコンポーネント
 */
const LongPressPopup = ({
  position,
  isLongPressPopupOpen,
  onCloseLongPressPopup,
  map,
  setNaviRoute,
  saveSpot
}) => {

  const [longPressAddress, setLongPressAddress] = useState(''); // 住所のstate
  const [longPressStations, setLongPressStations] = useState([]); // 最寄駅データのstate
  const [longPressElevation, setLongPressElevation] = useState(null); // 標高データのstate
  const [longPressWeather, setLongPressWeather] = useState(null); // 天気データのstate
  const [longPressDistanceFromCurrentLocation, setLongPressDistanceFromCurrentLocation] = useState(null); // 現在地からの距離のstate
  const [longPressMapCode, setLongPressMapCode] = useState(''); // マップコードのstate

  // 天気アイコンの表示フラグ
  const [iconVisible, setIconVisible] = useState(null);
  // アイコンURLが変わったら一旦表示フラグをtrueに（存在するなら出す）
  useEffect(() => {
    setIconVisible(!!longPressWeather?.icon);
  }, [longPressWeather?.icon]);

  // クリックしたDOMがこの要素の内側か？を判定するため（ポップアップ本体のDOM参照）
  const containerRef = useRef(null);

  // ピン用のref
  const markerRef = useRef(null);

  // 外側クリックで閉じる
  // ポップアップが開いていて、かつpositionがある時だけ監視
  useOnClickOutside({
    // 内側判定の基準となる要素のref
    containerRef,
    // 外側クリックだった場合に実行する関数
    onOutside: onCloseLongPressPopup,
    // trueのときだけ有効化
    enabled: Boolean(isLongPressPopupOpen && position),
    // ここをクリックした場合は無視するセレクタ群（挙動と外側判定の干渉避け）：なし
    ignoreSelectors: [],
  });

  // 長押し地点に地図の中心を移動し、ピンを立てる
  useEffect(() => {
    if (map && position) {
      map.jumpTo({
        center: [position.lng, position.lat],
        duration: JUMPTO_DURATION_MS
      });

      // 既存のマーカーがあれば削除
      if (markerRef.current) {
        markerRef.current.remove();
      }
      // 新しいマーカー（ピン）を作成
      const marker = new window.maplibregl.Marker({
        color: LONG_PRESS_MARKER_COLOR
      })
        .setLngLat([position.lng, position.lat])
        .addTo(map);

      // マーカーをrefに保存
      markerRef.current = marker;
    }
    // コンポーネントのアンマウント時またはポップアップが閉じられた時にマーカーを削除
    return () => {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
    };
  }, [map, position, isLongPressPopupOpen]);

  // 住所取得用の関数
  useEffect(() => {
    if (position) {
      // 住所を取得
      fetchAddress(position.lng, position.lat)
        .then(data => {
          setLongPressAddress(data?.value?.body?.where?.full?.address || '');
        })
        .catch(error => {
          setLongPressAddress('');
        });
    }
  }, [position]);

  // 天気情報取得用のuseEffect
  useEffect(() => {
    if (position) {
      // 天気情報を取得
      fetchWeather(position.lat, position.lng)
        .then(data => {
          // 受け取ったdataの天気コードをもとに、アイコンURLを取得
          const weatherIconUrl = `${WEATHER_ICON_BASE_URL}/${data.weather_code}${WEATHER_ICON_EXT}`;

          setLongPressWeather({
            temperature: data.now_temperature || TEMPERATURE_UNKNOWN_LABEL,
            icon: weatherIconUrl,
          });
        })
        .catch(error => {
          setLongPressWeather(null);
        });
    }
  }, [position]);

  // 駅情報取得用のuseEffect
  useEffect(() => {
    if (position) {
      // 駅情報を取得
      fetchNearestStations(position.lat, position.lng)
        .then(data => {
          // データを受け取った後、距離をフォーマット
          const formattedData = data.map(station => {
            // 距離を数値に変換してからフォーマット
            const formattedDistance = formatDistance(parseFloat(station.distance));
            return {
              name: station.st_name,
              distance: formattedDistance
            };
          });
          setLongPressStations(formattedData);
        })
        .catch(error => {
          setLongPressStations([]);
        });
    }
  }, [position]);

  // 標高情報取得のuseEffect
  useEffect(() => {
    if (position) {
      // 初期値をnullに設定し、ローディング状態を表示
      setLongPressElevation(null);

      // 標高を取得
      fetchElevation(position.lat, position.lng)
        .then(data => {
          // APIから返ってきた標高をセット
          setLongPressElevation(data);
        })
        .catch(error => {
          setLongPressElevation(ELEVATION_UNKNOWN_LABEL);
        });
    }
  }, [position]);

  // 現在地からの距離取得のuseEffect
  useEffect(() => {
    if (position) {
      // 現在地からの距離を取得
      navigator.geolocation.getCurrentPosition(currentPosition => {
        const distance = calculateDistance(
          currentPosition.coords.latitude,
          currentPosition.coords.longitude,
          position.lat,
          position.lng
        );
        setLongPressDistanceFromCurrentLocation(distance);
      });
    }
  }, [position]);

  // マップコード取得のuseEffect
  useEffect(() => {
    if (position) {
      const code = fetchMapCode(position.lat, position.lng)
      setLongPressMapCode(code);
    }
  }, [position]);

  // ポップアップが開いていない、または位置情報がない場合は何も表示しない
  if (!isLongPressPopupOpen || !position) {
    return null;
  }

  // 緯度経度をコピー
  const handleCopyCoordinates = () => {
    const text = `${position.lat.toFixed(DEFAULT_COORD_PRECISION)}, ${position.lng.toFixed(DEFAULT_COORD_PRECISION)}`;
    copyToClipboard(text, CLIPBOARD_KIND_COORD)
      .then(result => {
        if (result?.success && result?.message) {
          alert(result.message);
        }
      })
      .catch(() => {
      });
  };

  // 距離計算関数
  function calculateDistance(lat1, lng1, lat2, lng2) {
    const R = EARTH_RADIUS_KM; // 地球の半径（km）
    const dLat = (lat2 - lat1) * DEG_TO_RAD;
    const dLng = (lng2 - lng1) * DEG_TO_RAD;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * DEG_TO_RAD) * Math.cos(lat2 * DEG_TO_RAD) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  // マップコードをコピー（mapcode:xxx の形式）
  const handleCopyMapcode = () => {
    if (!longPressMapCode) {
      return;
    }
    const text = `${MAPCODE_PREFIX}${longPressMapCode}`;
    copyToClipboard(text, CLIPBOARD_KIND_MAPCODE)
      .then(result => {
        if (result?.success && result?.message) {
          alert(result.message);
        }
      })
      .catch(() => {
      });
  };

  return (
    <>
      <div
        id="location-detail-panel"
        className="location-detail-panel"
        ref={containerRef}
      >
        {/* 住所 */}
        <div className="detail-header">
          <h3>
            {longPressAddress || ADDRESS_GETTING_LABEL}
          </h3>
          <button
            id="close-detail-btn"
            className="close-search-result-btn"
            onClick={onCloseLongPressPopup}
          >
            <i
              className="svg-icon-black"
              style={{ backgroundImage: `url(${ICON_URLS.close})` }}>
            </i>
          </button>
        </div>
        <div className="detail-content"
        >

          {/* 詳細情報グリッド */}
          <div className="detail-info-grid">

            {/* 最寄駅 */}
            <div className="detail-info-item">
              <div className="detail-info-icon">
                <i
                  className="svg-icon-blue"
                  style={{ backgroundImage: `url(${ICON_URLS.directionsTrain})` }}>
                </i>
              </div>
              <div className="detail-info-content">
                <span
                  className="detail-info-value"
                  id="detail-station"
                >
                  {/* 加工して出力 */}
                  {(() => {
                    let message;
                    if (longPressStations == null) {
                      // データがない場合（初期状態）
                      message = STATION_GETTING_LABEL;
                    } else if (typeof longPressStations === 'string') {
                      // stationsが文字列の場合（ローディングメッセージなど）
                      message = longPressStations;
                    } else if (Array.isArray(longPressStations) && longPressStations.length > 0) {
                      // 駅がある場合：駅名と距離を "駅名 (約距離)" 形式にフォーマット
                      message = longPressStations
                        .map(station => `${station.name} (${STATION_DISTANCE_PREFIX}${station.distance})`)
                        .join(STATION_LIST_SEPARATOR);
                    } else {
                      // その他のケース 近くに駅がない場合など
                      message = STATION_UNKNOWN_LABEL;
                    }
                    return message;
                  })()}
                </span>
              </div>
            </div>

            {/* 標高情報 */}
            <div className="detail-info-item">
              <div className="detail-info-icon">
                <i
                  className="svg-icon-blue"
                  style={{ backgroundImage: `url('${ICON_URLS.mountain}')` }}
                ></i>
              </div>
              <div className="detail-info-content">
                <span
                  className="detail-info-value"
                  id="detail-elevation"
                >
                  {/* 加工して出力 */}
                  {(() => {
                    if (longPressElevation === null) {
                      return ELEVATION_GETTING_LABEL;
                    }
                    if (longPressElevation === ELEVATION_UNKNOWN_LABEL) {
                      return ELEVATION_UNKNOWN_LABEL;
                    }
                    return `${longPressElevation}${UNIT_METER}`;
                  })()}
                </span>
              </div>
            </div>

            {/* 天気 */}
            <div className="detail-info-item">
              <div className="detail-info-icon">
                <img
                  id="detail-weather-icon"
                  className="weather-icon"
                  src={longPressWeather?.icon}
                  alt={WEATHER_ICON_ALT}
                  // 読み込みエラーで即座に非表示にする（壊れ画像アイコンを見せない）
                  onError={(e) => { e.currentTarget.style.display = 'none'; setIconVisible(false); }}
                  style={{
                    display: iconVisible ? 'inline-block' : 'none',
                  }}
                />
              </div>

              {/* 気温 */}
              <div className="detail-info-content">
                <span
                  className="detail-info-value"
                  id="detail-weather"
                >
                  {longPressWeather?.temperature ?? TEMPERATURE_UNKNOWN_LABEL}{DEGREE_SYMBOL}
                </span>
              </div>
            </div>

            {/* 距離（現在地から） */}
            <div
              className="detail-info-item"
              id="detail-distance-container"
              style={{ display: "flex" }}
            >
              <div className="detail-info-icon">
                <i
                  className="svg-icon-blue"
                  style={{ backgroundImage: `url(${ICON_URLS.nearMe})` }}>
                </i>
              </div>
              <div className="detail-info-content">
                <span
                  className="detail-info-value"
                  id="detail-distance"
                >
                  {longPressDistanceFromCurrentLocation == null
                    ? DISTANCE_GETTING_LABEL
                    : `${DISTANCE_FROM_CURRENT_LOCATION_LABEL} ${longPressDistanceFromCurrentLocation.toFixed(DISTANCE_KM_DECIMALS)}${UNIT_KILOMETER}`}
                </span>
              </div>
            </div>

            {/* 緯度・経度 */}
            <div className="detail-info-item">
              <div className="detail-info-icon">
                <i
                  className="svg-icon-blue"
                  style={{ backgroundImage: `url(${ICON_URLS.location})` }}>
                </i>
              </div>
              <div className="detail-info-content">
                <span
                  className="detail-info-value coords"
                  id="detail-latlng"
                  onClick={handleCopyCoordinates}
                >
                  {position.lat.toFixed(DEFAULT_COORD_PRECISION)},{position.lng.toFixed(DEFAULT_COORD_PRECISION)}
                </span>
              </div>
            </div>

            {/* マップコード */}
            <div className="detail-info-item">
              <div className="detail-info-icon">
                <i
                  className="svg-icon-blue"
                  style={{ backgroundImage: `url(${ICON_URLS.map})` }}>
                </i>
              </div>
              <div className="detail-info-content">
                <span
                  className="detail-info-value"
                  id="detail-mapcode"
                  onClick={handleCopyMapcode}
                >
                  {MAPCODE_LABEL}{longPressMapCode ? longPressMapCode : MAPCODE_GETTING_LABEL}
                </span>
              </div>
            </div>

          </div>

          {/* アクションボタン */}
          <div className="detail-actions">
            <button
              className="action-btn primary"
              id="save-location-btn"
              onClick={() => {
                saveSpot({ lat: position.lat, lng: position.lng });
              }}
            >
              <i
                className="svg-icon-white"
                style={{ backgroundImage: `url(${ICON_URLS.star})` }}>
              </i>
              {BUTTON_LABEL_SAVE}
            </button>

            <button
              className="action-btn"
              id="share-location-btn"
              // 長押し地点のURLをコピー
              onClick={async () => {
                try {
                  copyToClipboard(window.location.href, CLIPBOARD_KIND_URL)
                    .then(result => {
                      if (result?.success && result?.message) {
                        alert(result.message);
                      }
                    })
                } catch (err) {
                }
                onCloseLongPressPopup();
              }}
            >
              <i
                className="svg-icon-white"
                style={{ backgroundImage: `url(${ICON_URLS.share})` }}>
              </i>
              {BUTTON_LABEL_SHARE}
            </button>

            <button
              className="action-btn"
              id="route-location-btn"
              onClick={() => {
                setNaviRoute({ name: longPressAddress || "住所不明", lat: position.lat, lon: position.lng });
                onCloseLongPressPopup();
              }}
            >
              <i
                className="svg-icon-white"
                style={{ backgroundImage: `url(${ICON_URLS.arrowTopRight})` }}>
              </i>
              {BUTTON_LABEL_ROUTE}
            </button>
          </div>
        </div>
      </div>

    </>
  );
};

export default LongPressPopup;
